// MeusLivros.js — "E-books": a estante do cliente.
//
// ================= O QUE ESTA TELA É, DESDE 04/09/2026 =================
//
// Ela deixou de ser "os livros que eu comprei". Agora é a união de quatro
// conjuntos, e o servidor é quem faz o recorte (GET /minha-estante):
//
//   comprei o e-book          → origem "compra"
//   comprei o impresso        → origem "brinde_fisico" (leitura de presente)
//   li 10% ou mais pelo Clube → origem "clube"
//   salvei na página do Clube → origem "clube" (desde 10/09/2026; `salvo`)
//
// O terceiro e o quarto conjuntos são o motivo da mudança: quem assina e não
// compra nada tinha uma estante vazia enquanto lia o acervo inteiro.
//
// Como consequência, o bloco "Continue lendo" saiu da aba do Clube e entrou
// aqui — depois do critério novo, ESTA LISTA JÁ É a lista de livros com
// progresso. Manter as duas seria a mesma lista em dois lugares com regras de
// entrada diferentes, ou seja, duas listas que um dia divergem.
//
// ================= A ESTANTE VIROU UMA ESTANTE (11/09/2026) =================
//
// Com quatro portas de entrada, a lista passou de "os três livros que eu
// comprei" para dezenas — e uma coluna de cartões largos, um por linha, é uma
// rolagem sem fim onde nada se acha. O dono pediu, e esta versão tem:
//
//   busca ................ título, subtítulo, autor e etiqueta
//   filtros .............. Todos / Comprados / Salvos / Lendo + as etiquetas
//   grade de dois ........ cartões mais quadrados, dois por linha
//   paginação ............ a mesma de /livros e /autores
//   emoji ................ a marca pessoal, na quina de cima à esquerda
//   etiqueta ............. o rótulo que a pessoa cria, na quina à direita
//
// O emoji e a etiqueta são do USUÁRIO, não do livro: dois leitores marcam o
// mesmo título de jeitos diferentes (ver EstanteController::marcar).
//
// ================= A TELA NÃO DECIDE PERMISSÃO =================
//
// `pode_ler`, `pode_baixar`, `mostrar_comprar` e `mensagem` chegam prontos. O
// que está desenhado abaixo é só a matriz de estados do servidor.
//
// O "Baixar" APARECE mesmo desabilitado, e isso é deliberado: botão ausente não
// ensina nada, botão cinza com `title` explica por que a assinatura dá leitura e
// não arquivo — que é a diferença entre R$ 50/mês e R$ 40 o título.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useEstante from "../../../hooks/useEstante";
import useMinhasAvaliacoes from "../../../hooks/useMinhasAvaliacoes";
import useClube from "../../../hooks/useClube";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useModal } from "../../../services/context/ModalContext";
import { COINS_POR_AVALIACAO } from "../../../config/coins";
import { message } from "../../utils/toast";
import Pagination from "../../utils/Pagination";
import SeletorEmoji from "../../ui/SeletorEmoji";
import EtiquetaDoLivro from "./EtiquetaDoLivro";
import useBarraGrudada, { CLASSES_ESCONDIDA, CLASSES_SUMICO } from "../../../hooks/useBarraGrudada";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

// Dois por linha, três linhas. Múltiplo de 2 para nenhuma página terminar com
// meia fileira.
//
// Era 8, e com 8 livros na estante a paginação simplesmente não aparecia — o
// controle some sozinho quando só existe uma página (Pagination.js), e o dono
// leu isso como "faltou a paginação". Seis também encurta a rolagem: seis
// cartões cabem quase inteiros numa tela de notebook.
const POR_PAGINA = 6;

const reais = (centavos) =>
  ((Number(centavos) || 0) / 100).toFixed(2).replace(".", ",");

// Sem centavos quando o valor é redondo: "R$ 50" ocupa menos e comunica igual.
const reaisCurto = (centavos) => {
  const v = (Number(centavos) || 0) / 100;
  return Number.isInteger(v) ? String(v) : reais(centavos);
};

// O preço de catálogo vem do servidor como "30.00" (decimal do banco). Escrever
// isso na tela dava "Valor: R$ 30.00", com ponto, no meio de uma loja que usa
// vírgula em todo lugar.
const precoBR = (valor) => {
  const n = parseFloat(valor);
  return isNaN(n) ? null : n.toFixed(2).replace(".", ",");
};

const semAcento = (s) =>
  String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// "null" e "undefined" não são subtítulo. O servidor já devolve nulo desde
// 11/09/2026 (e a migração limpou os 60 títulos que estavam assim), mas a
// guarda fica: um cache velho de resposta não pode reescrever "null" na tela.
const subtituloDe = (livro) => {
  const s = String(livro?.subtitulo ?? "").trim();
  return s === "" || ["null", "undefined", "nan"].includes(s.toLowerCase()) ? null : s;
};

const nomesDosAutores = (livro) =>
  (Array.isArray(livro?.autores) ? livro.autores : []).filter(Boolean);

const BOTAO =
  "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors";

function MeusLivros() {
  const {
    livros: livrosDaEstante,
    etiquetas,
    etiquetaMax,
    marcar,
    clube,
    downloadHabilitado,
    loading,
    erro,
    baixar,
    baixandoId,
  } = useEstante();

  // Do `useClube` vem SÓ o tamanho do acervo, que a estante não carrega. Se a
  // resposta de "assinante" também viesse daqui, as duas fontes poderiam
  // divergir e a caixa de venda apareceria para quem já paga.
  const { status: statusClube } = useClube();
  const assinante = !!clube?.ativa;

  // Esta é a única lista do site onde estão exatamente os livros que a pessoa
  // pode avaliar. Sem o convite aqui, o bônus de coins depende de ela lembrar
  // sozinha de voltar à página de cada livro.
  const { avaliacoes } = useMinhasAvaliacoes();
  const jaAvaliados = useMemo(
    () => new Set(avaliacoes.map((a) => String(a.livro?.id))),
    [avaliacoes]
  );

  // ===== TIRAR DA ESTANTE UM LIVRO SALVO DO CLUBE =====
  //
  // Só o que entrou por "Salvar nos meus e-books" e por nada mais
  // (`removivel`, do servidor). O livro some da lista NA HORA, por um filtro
  // local, em vez de recarregar a estante inteira.
  const [tirados, setTirados] = useState(() => new Set());
  const [tirandoId, setTirandoId] = useState(null);
  const livros = useMemo(
    () => livrosDaEstante.filter((l) => !tirados.has(l.id)),
    [livrosDaEstante, tirados]
  );

  const tirarDaEstante = async (book) => {
    if (tirandoId) return;
    setTirandoId(book.id);
    try {
      const r = await fetch(`${API_URL}minha-estante/livro/${book.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Accept: "application/json",
        },
      });
      if (!r.ok) throw new Error();
      setTirados((s) => new Set(s).add(book.id));
      message.success("Livro tirado dos seus E-books.");
    } catch {
      message.error("Não foi possível tirar o livro agora. Tente de novo.");
    } finally {
      setTirandoId(null);
    }
  };

  const { adicionarItem, itemJaNoCarrinho } = useContext(CarrinhoContext);
  const { openModal } = useModal();
  const [erroDownload, setErroDownload] = useState(null);

  // ===== Busca, filtro e página =====
  const [busca, setBusca] = useState("");
  // "todos" | "comprados" | "salvos" | "lendo" | id da etiqueta (número)
  const [filtro, setFiltro] = useState("todos");
  const [pagina, setPagina] = useState(1);
  // O livro cujo seletor de emoji está aberto.
  const [emojiDe, setEmojiDe] = useState(null);
  // A trava é POR LIVRO, e não uma só para a tela: com uma trava global, pôr
  // etiqueta no livro B enquanto o A ainda salvava era descartado em silêncio —
  // o popover fechava, nada era enviado e nenhum aviso aparecia.
  const [marcando, setMarcando] = useState(() => new Set());

  // A barra gruda logo abaixo do cabeçalho do perfil, que também gruda: o topo
  // é a borda de baixo dele (--topo-perfil) mais a altura medida dele
  // (--altura-cabecalho-perfil), as duas declaradas no Perfil.js. E ela some
  // pela mesma regra da barra do perfil (ver useBarraGrudada).
  const { barraRef, sentinelaRef, esconder: esconderBarra } = useBarraGrudada();

  const salvarMarca = async (book, mudancas) => {
    if (marcando.has(book.id)) return;
    setMarcando((s) => new Set(s).add(book.id));
    try {
      await marcar(book.id, mudancas);
    } catch (e) {
      message.error(e.message || "Não foi possível salvar agora.");
    } finally {
      setMarcando((s) => {
        const novo = new Set(s);
        novo.delete(book.id);
        return novo;
      });
    }
  };

  // ================= O QUE A ESTANTE JA CUSTOU =================
  //
  // A conta soma o preco dos e-books COMPRADOS, e so eles. O brinde pelo
  // impresso nao foi pago como e-book, e o livro que entrou por leitura do
  // Clube nao foi pago de forma nenhuma.
  //
  // O valor e o do CATALOGO DE HOJE, e nao o da nota fiscal — e o mesmo numero
  // que a propria tela mostra ao lado ("Valor: R$ ...").
  const comprados = useMemo(
    () => livros.filter((l) => l.origem === "compra"),
    [livros]
  );

  const investido = useMemo(
    () => comprados.reduce((t, b) => t + (parseFloat(b.preco) || 0), 0),
    [comprados]
  );

  const emLeitura = useMemo(() => livros.filter((l) => l.progresso).length, [livros]);
  const salvos = useMemo(() => livros.filter((l) => l.salvo).length, [livros]);

  // ===== A lista que a tela mostra =====
  //
  // Filtro e busca são independentes e se somam: "Comprados" + "direito" é o
  // que a pessoa espera de qualquer catálogo. A etiqueta entra na busca porque
  // é ela que a pessoa lembra de ter escrito.
  const filtrados = useMemo(() => {
    const termo = semAcento(busca).trim();

    return livros.filter((l) => {
      if (filtro === "comprados" && l.origem !== "compra" && l.origem !== "brinde_fisico") return false;
      if (filtro === "salvos" && !l.salvo) return false;
      if (filtro === "lendo" && !l.progresso) return false;
      if (typeof filtro === "number" && Number(l.etiqueta_id) !== filtro) return false;

      if (!termo) return true;

      const etiqueta = etiquetas.find((e) => e.id === Number(l.etiqueta_id))?.nome || "";
      const alvo = semAcento(
        [l.titulo, subtituloDe(l), nomesDosAutores(l).join(" "), etiqueta].filter(Boolean).join(" ")
      );
      return alvo.includes(termo);
    });
  }, [livros, filtro, busca, etiquetas]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * POR_PAGINA;
  const itensPagina = filtrados.slice(inicio, inicio + POR_PAGINA);

  // Filtrar da página 3 com uma página de resultado mostraria grade vazia.
  useEffect(() => { setPagina(1); }, [filtro, busca]);

  // A etiqueta selecionada pode DEIXAR DE EXISTIR debaixo do filtro: basta
  // tirá-la do último livro que a usava, e o servidor apaga a órfã. Sem isto, o
  // filtro continuava apontando para um id que não existe mais — nenhum chip
  // aceso, nenhum livro na grade e um "Nada encontrado" sem culpado à vista.
  useEffect(() => {
    if (typeof filtro === "number" && !etiquetas.some((e) => e.id === filtro)) {
      setFiltro("todos");
    }
  }, [etiquetas, filtro]);

  const precoClube = Number(clube?.preco_centavos) || 0;
  const diasClube = Number(clube?.dias) || 30;
  const acervoClube = Number(statusClube?.acervo_total) || 0;

  // DOIS LIVROS E O PISO: com um livro so a frase "voce ja gastou R$ 40" nao
  // compara nada, e com zero ela acusa a pessoa de um gasto que nao existe.
  const mostrarComparacao =
    !assinante &&
    comprados.length >= 2 &&
    investido > 0 &&
    precoClube > 0 &&
    acervoClube > 0;

  // Por que o "Baixar" está cinza. Os três motivos levam a decisões diferentes
  // de quem lê — desligado no site inteiro, dono do papel, ou assinante.
  const motivoDoDownload = (book) => {
    if (book.pode_baixar) return "Baixar o PDF deste livro";
    if (!downloadHabilitado) {
      return "O download está desativado. Os livros são lidos aqui no site, com marcador de página e anotações.";
    }
    if (book.origem === "brinde_fisico") {
      return "Você é dono do livro impresso, não do arquivo digital. Comprando o e-book, o download libera.";
    }
    return "O arquivo é de quem compra o e-book. A assinatura dá a leitura no site.";
  };

  const aoBaixar = async (book) => {
    setErroDownload(null);
    try {
      await baixar(book);
    } catch (e) {
      const texto = e?.message || "Não foi possível baixar agora.";
      setErroDownload({ id: book.id, texto });
      message.error(texto);
    }
  };

  // O PREÇO VEM DO SERVIDOR, em centavos (`preco_ebook_centavos`, do
  // PrecoService): é o mesmo cálculo que o checkout confere. Mandar o cheio
  // quando há promoção faz o pedido inteiro ser recusado com "o preço de um dos
  // livros não confere" — e a recusa só aparece no fim, depois do pagamento.
  const aoComprar = (book) => {
    const centavos = Number(book.preco_ebook_centavos) || 0;
    if (centavos <= 0) return;
    const venda = centavos / 100;
    const catalogo = parseFloat(book.preco);
    const item = {
      id: book.id,
      titulo: book.titulo,
      image: book.image,
      preco: venda,
      ...(catalogo > venda ? { preco_original: catalogo } : {}),
      formato: "ebook",
      quantidade: 1,
    };
    adicionarItem(item);
    openModal(item);
  };

  // Os filtros do topo. Os três primeiros só aparecem quando têm o que
  // mostrar: um filtro que devolve lista vazia ensina a ignorar a barra.
  const filtros = [
    { chave: "todos", rotulo: "Todos", n: livros.length },
    { chave: "comprados", rotulo: "Comprados", n: livros.filter((l) => l.origem === "compra" || l.origem === "brinde_fisico").length },
    { chave: "salvos", rotulo: "Salvos", n: salvos },
    { chave: "lendo", rotulo: "Lendo", n: emLeitura },
  ].filter((f) => f.chave === "todos" || f.n > 0)
    .concat(etiquetas.filter((e) => e.livros > 0).map((e) => ({ chave: e.id, rotulo: e.nome, n: e.livros })));

  return (
    <div>
      <div className="mb-4">
        <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] flex items-center gap-2">
          <i className="bi bi-collection-play" /> E-books
        </h2>
        {!loading && !erro && livros.length > 0 && (
          <p className="text-white/50 text-sm mt-1.5">
            {livros.length} {livros.length === 1 ? "livro" : "livros"}
            {emLeitura > 0 && <> · {emLeitura} em leitura</>}
          </p>
        )}
      </div>

      {/* A comparacao fica ACIMA da lista e nao no fim: quem rola ate o fim da
          propria estante ja e quem menos precisa ser convencido. */}
      {mostrarComparacao && (
        <Link
          to={main + "clube"}
          className="block mb-5 rounded-2xl border border-insigne-orange/40 bg-insigne-orange/10 p-4 md:p-5
                     hover:bg-insigne-orange/15 transition-colors"
        >
          <div className="flex items-start gap-3">
            <i className="bi bi-calculator text-insigne-orange text-xl shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm md:text-base font-semibold leading-snug">
                Estes {comprados.length} e-books somam{" "}
                <span className="text-insigne-orange">
                  R$ {investido.toFixed(2).replace(".", ",")}
                </span>
                .
              </p>
              <p className="text-white/60 text-sm mt-1 leading-relaxed">
                O Clube Insigne dá {acervoClube} livros por R$ {reaisCurto(precoClube)} a
                cada {diasClube} dias, e não renova sozinho.
              </p>
              <span className="link-sublinhado text-sm mt-2 inline-block">Ver o Clube</span>
            </div>
          </div>
        </Link>
      )}

      {loading ? (
        <div className="text-center text-white/50 py-16">Carregando seus livros...</div>
      ) : erro ? (
        <div className="text-center text-red-300 py-16">Ocorreu um erro ao carregar seus livros.</div>
      ) : livros.length === 0 ? (
        /* O texto fala das DUAS portas de entrada. Falando só de compra, o
           assinante que ainda não abriu nada leria "você não tem e-books" e
           concluiria que a assinatura não vale para ele. */
        <div className="flex flex-col items-center justify-center text-center py-16">
          <i className="bi bi-book text-5xl text-white/30 mb-4" />
          <h3 className="text-lg font-bold">Sua estante está vazia</h3>
          <p className="text-white/50 text-sm mt-1 max-w-md leading-relaxed">
            Aqui ficam os e-books que você compra e também os livros do Clube
            Insigne que você salvar ou começar a ler.
          </p>
          <Link
            to={main + "livros"}
            className="mt-5 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold
                       bg-insigne-orange text-paper hover:bg-insigne-orange-dark transition-colors"
          >
            <i className="bi bi-search" /> Explorar o catálogo
          </Link>
        </div>
      ) : (
        <>
          {/* ===== A BARRA QUE ACOMPANHA A ROLAGEM =====
              Busca e filtros ficam à mão no meio da lista, como em /livros. Ela
              gruda ABAIXO do cabeçalho do perfil, que também gruda: o topo é a
              borda de baixo dele (--topo-perfil) mais a altura medida
              (--altura-cabecalho-perfil), as duas declaradas no Perfil.js.

              `z-20` fica abaixo do `z-30` do cabeçalho do perfil e acima dos
              cards. Fundo opaco, senão as capas passam por baixo do campo. */}
          {/* A âncora da paginação é ESTE ponto, e não a barra: a barra é
              `sticky`, e quando ela está grudada no topo um `scrollIntoView`
              nela não rola nada — a pessoa trocava de página e continuava
              olhando o fim da lista. A margem de rolagem desconta o cabeçalho
              do site e a própria barra. */}
          <div
            id="estante-topo"
            ref={sentinelaRef}
            aria-hidden="true"
            className="h-0 scroll-mt-24 md:scroll-mt-[280px]"
          />

          <div
            ref={barraRef}
            className={"mb-4 md:sticky md:z-20 md:-mx-4 md:px-4 md:py-3 md:bg-insigne-bg " +
                       "md:top-[calc(var(--topo-perfil,0px)_+_var(--altura-cabecalho-perfil,0px))] " +
                       CLASSES_SUMICO + " " +
                       (esconderBarra ? CLASSES_ESCONDIDA : "")}
          >
            <div className="relative">
              <i className="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-white/40 pointer-events-none" />
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por título, autor ou etiqueta..."
                aria-label="Buscar nos seus e-books"
                className="foco-laranja w-full rounded-lg border border-line bg-white/5 py-2.5 pl-10 pr-3
                           text-sm text-white placeholder:text-white/40 outline-none
                           transition-colors focus:border-insigne-orange/50 focus:bg-white/[0.07]"
              />
            </div>

            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {filtros.map((f) => (
                <button
                  key={String(f.chave)}
                  type="button"
                  onClick={() => setFiltro(f.chave)}
                  aria-pressed={filtro === f.chave}
                  className={
                    "foco-laranja inline-flex items-center gap-1.5 rounded-controle border px-3 py-1 text-xs font-semibold " +
                    "transition-colors " +
                    (filtro === f.chave
                      ? "border-insigne-orange bg-insigne-orange text-paper"
                      : "border-line bg-white/5 text-white/70 hover:bg-white/10 hover:text-white")
                  }
                >
                  {typeof f.chave === "number" && <i className="bi bi-tag-fill text-[10px]" />}
                  {f.rotulo}
                  <span className={filtro === f.chave ? "text-paper/70" : "text-white/35"}>{f.n}</span>
                </button>
              ))}
            </div>
          </div>

          {filtrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-14">
              <i className="bi bi-search text-4xl text-white/25 mb-3" />
              <h3 className="font-bold">Nada encontrado</h3>
              <p className="text-white/50 text-sm mt-1">
                {busca ? <>Nenhum livro seu combina com "{busca}".</> : "Este filtro está vazio."}
              </p>
              <button
                type="button"
                onClick={() => { setBusca(""); setFiltro("todos"); }}
                className="btn-solido mt-4 rounded-controle px-5 py-2 text-sm font-semibold"
              >
                Limpar busca e filtros
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-white/45 mb-3">
                Mostrando {inicio + 1}–{Math.min(inicio + POR_PAGINA, filtrados.length)} de{" "}
                {filtrados.length} {filtrados.length === 1 ? "livro" : "livros"}
              </p>

              {/* Dois por linha a partir de 640px. `gap-y-7` dá espaço para o
                  emoji e a etiqueta, que se projetam para fora da quina do
                  card — sem ela, a marca de uma fileira encosta no card de
                  baixo. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-7">
                {itensPagina.map((book) => (
                  <CardDoLivro
                    key={book.id}
                    book={book}
                    etiquetas={etiquetas}
                    etiquetaMax={etiquetaMax}
                    salvandoMarca={marcando.has(book.id)}
                    onEmoji={() => setEmojiDe(book)}
                    onEtiqueta={(nome) => salvarMarca(book, { etiqueta: nome ?? "" })}
                    jaAvaliou={jaAvaliados.has(String(book.id))}
                    baixando={baixandoId === book.id}
                    motivoDownload={motivoDoDownload(book)}
                    onBaixar={() => aoBaixar(book)}
                    noCarrinho={itemJaNoCarrinho(book.id, "ebook")}
                    onComprar={() => aoComprar(book)}
                    tirando={tirandoId === book.id}
                    onTirar={() => tirarDaEstante(book)}
                    erroDownload={erroDownload?.id === book.id ? erroDownload.texto : null}
                  />
                ))}
              </div>

              {/* `rolarAte`: trocar de página volta ao topo da LISTA, e não ao
                  topo do perfil, que fica acima da barra de busca. */}
              <Pagination
                pagina={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                rolarAte="estante-topo"
              />
            </>
          )}
        </>
      )}

      {/* O seletor de emoji é um só para a tela toda: montar um por card seriam
          oito listas de 1.200 emojis no DOM, e só uma pode estar aberta. */}
      <SeletorEmoji
        aberto={!!emojiDe}
        atual={emojiDe?.emoji || null}
        onFechar={() => setEmojiDe(null)}
        onEscolher={(emoji) => {
          const alvo = emojiDe;
          setEmojiDe(null);
          if (alvo) salvarMarca(alvo, { emoji: emoji ?? "" });
        }}
      />
    </div>
  );
}

/**
 * Um livro da estante.
 *
 * MAIS QUADRADO QUE LARGO (pedido do dono, 11/09/2026): a capa pequena à
 * esquerda, o texto à direita e as ações embaixo, dentro de uma coluna que é
 * metade da largura antiga. Os botões viraram ícones quando o rótulo não cabe
 * — "Baixar PDF" escrito ao lado de "Continuar lendo" não entra em 330px, e o
 * `title` de cada ícone continua explicando o que ele faz.
 */
const CardDoLivro = ({
  book,
  etiquetas,
  etiquetaMax,
  salvandoMarca,
  onEmoji,
  onEtiqueta,
  jaAvaliou,
  baixando,
  motivoDownload,
  onBaixar,
  noCarrinho,
  onComprar,
  tirando,
  onTirar,
  erroDownload,
}) => {
  const progresso = book.progresso;
  const pct = Number(progresso?.percentual) || 0;
  const precoEbook = Number(book.preco_ebook_centavos) || 0;
  const minimoAvaliar = Number(book.percentual_minimo_avaliar) || 50;
  const subtitulo = subtituloDe(book);
  const autores = nomesDosAutores(book);
  const valor = book.origem === "compra" ? precoBR(book.preco) : null;

  return (
    <article className="relative flex flex-col cartao p-4">
      {/* ===== A MARCA PESSOAL, na quina de cima à esquerda =====
          Redondo de propósito: é o único controle redondo do card, então o
          olho o encontra sem procurar. Vazio, ele é um contorno tracejado com
          um rostinho apagado — cheio, é o emoji em tamanho de leitura. */}
      <button
        type="button"
        onClick={onEmoji}
        disabled={salvandoMarca}
        title={book.emoji ? "Trocar o emoji deste livro" : "Pôr um emoji neste livro"}
        aria-label={book.emoji ? `Emoji do livro: ${book.emoji}. Trocar` : "Pôr um emoji neste livro"}
        className={
          // `z-10`: a barra de busca gruda com `z-20` e a grade vem depois
          // dela no DOM — no empate, o botão desenhava por cima da barra.
          "absolute -top-3 -left-3 z-10 flex h-11 w-11 items-center justify-center rounded-full border-2 " +
          "text-xl leading-none transition-colors disabled:opacity-60 " +
          (book.emoji
            ? "border-insigne-orange bg-insigne-navy-deep"
            : "border-dashed border-white/25 bg-insigne-navy-deep text-white/35 hover:border-white/50 hover:text-white/70")
        }
      >
        {salvandoMarca ? <i className="bi bi-arrow-repeat animate-spin text-base" />
          : book.emoji || <i className="bi bi-emoji-smile text-base" />}
      </button>

      <EtiquetaDoLivro
        etiquetaId={Number(book.etiqueta_id) || null}
        etiquetas={etiquetas}
        max={etiquetaMax}
        salvando={salvandoMarca}
        onEscolher={onEtiqueta}
      />

      <div className="flex gap-3.5 pt-3">
        <div className="w-[68px] h-[92px] shrink-0 overflow-hidden rounded-lg bg-insigne-navy-deep
                        flex items-center justify-center">
          {book.image ? (
            <img
              src={`${API_URL}public/${book.image}`}
              alt={book.titulo}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <i className="bi bi-book text-white/30 text-xl" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm md:text-[15px] leading-snug line-clamp-2" title={book.titulo}>
            {book.titulo}
          </h3>
          {subtitulo && (
            <p className="text-white/45 text-xs mt-0.5 leading-snug line-clamp-1" title={subtitulo}>
              {subtitulo}
            </p>
          )}
          {autores.length > 0 && (
            <p className="text-white/35 text-xs mt-1 truncate" title={autores.join(", ")}>
              {autores.join(", ")}
            </p>
          )}

          {/* O "Valor" só aparece no que foi comprado. No livro do Clube ele
              seria o preço de uma compra que não houve. */}
          {valor && <p className="text-white/45 text-xs mt-1">Valor: R$ {valor}</p>}

          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1.5">
            {/* "Compra confirmada" seria mentira no presente e no Clube: o
                rótulo diferente é o que explica, sem ticket de suporte, por que
                este título não baixa. A origem vem do servidor. */}
            {book.origem === "brinde_fisico" ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-200">
                <i className="bi bi-gift-fill" /> Presente do impresso
              </span>
            ) : book.origem === "clube" ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-insigne-orange">
                {book.salvo && !progresso ? (
                  <><i className="bi bi-bookmark-fill" /> Salvo do Clube</>
                ) : (
                  <><i className="bi bi-stars" /> Lendo pelo Clube</>
                )}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                <i className="bi bi-check-circle-fill" /> Compra confirmada
              </span>
            )}

            {/* O convite some assim que o livro é avaliado. Quando ela ainda
                não abriu, o aviso EXPLICA o que falta — esconder o convite
                faria a regra dos 50% parecer defeito, e é ela que faz voltar a
                ler. */}
            {jaAvaliou ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-white/40">
                <i className="bi bi-star-fill" /> Você avaliou
              </span>
            ) : book.pode_avaliar ? (
              <Link
                to={main + "livros/" + book.id + "#opinioes"}
                className="inline-flex items-center gap-1.5 rounded-selo border border-insigne-orange/45
                           bg-insigne-orange/10 px-2 py-0.5 text-[11px] font-semibold text-insigne-orange
                           hover:bg-insigne-orange hover:text-paper transition-colors"
              >
                <i className="bi bi-star" /> Avaliar · +{COINS_POR_AVALIACAO}
              </Link>
            ) : book.origem === "clube" ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-white/40">
                <i className="bi bi-hourglass-split" /> Avaliar com {minimoAvaliar}% · você em {pct}%
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* A barra é o "Continue lendo" que veio da aba do Clube: ela mora na
          linha do livro, e a lista já vem ordenada com quem tem progresso na
          frente. `mt-auto` empurra o rodapé do card para baixo, e assim os
          botões das duas colunas ficam na mesma altura. */}
      <div className="mt-auto pt-3">
        {progresso && (
          <div className="mb-2.5">
            <div className="h-1.5 rounded-selo bg-white/10 overflow-hidden">
              <div className="h-full bg-insigne-orange" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[11px] text-white/40 mt-1">
              Página {progresso.ultima_pagina}
              {progresso.total_paginas > 0 && ` de ${progresso.total_paginas}`}
              {" · "}{pct}% lido
            </p>
          </div>
        )}

        <div className="flex items-stretch gap-2">
          {book.pode_ler ? (
            <Link
              to={main + "ler/" + book.id}
              className={BOTAO + " flex-1 bg-insigne-orange text-paper hover:bg-insigne-orange-dark"}
            >
              <i className="bi bi-book" />
              {progresso ? "Continuar lendo" : "Ler"}
            </Link>
          ) : (
            <button
              type="button"
              disabled
              title="Sua assinatura venceu. Compre o e-book ou renove o Clube para voltar a ler."
              className={BOTAO + " flex-1 border border-line text-white/40 cursor-not-allowed"}
            >
              <i className="bi bi-lock" /> Ler
            </button>
          )}

          {/* Ícone sozinho: o rótulo não cabe ao lado de "Continuar lendo" num
              card de meia largura, e o `title` diz por que ele está cinza. */}
          <button
            type="button"
            disabled={!book.pode_baixar || baixando}
            title={motivoDownload}
            aria-label={motivoDownload}
            onClick={onBaixar}
            className={
              "inline-flex w-11 items-center justify-center rounded-lg border text-base transition-colors " +
              (book.pode_baixar
                ? "border-insigne-orange/45 text-insigne-orange hover:bg-insigne-orange hover:text-paper disabled:opacity-60"
                : "border-line text-white/35 cursor-not-allowed")
            }
          >
            <i className={"bi " + (baixando ? "bi-arrow-repeat animate-spin" : "bi-download")} />
          </button>

          {book.removivel && (
            <button
              type="button"
              onClick={onTirar}
              disabled={tirando}
              title="Tirar este livro da estante"
              aria-label="Tirar este livro da estante"
              className="inline-flex w-11 items-center justify-center rounded-lg border border-line text-base
                         text-white/50 transition-colors hover:border-white/30 hover:text-white disabled:opacity-60"
            >
              <i className={"bi " + (tirando ? "bi-arrow-repeat animate-spin" : "bi-bookmark-x")} />
            </button>
          )}
        </div>

        {/* O "Comprar" aparece INCLUSIVE com o Clube ativo: é o upsell de quem
            já está lendo e gostou. Sem preço do servidor vira link para a
            página do livro — jogar no carrinho um item de R$ 0 derrubaria o
            pedido no checkout. */}
        {book.mostrar_comprar &&
          (noCarrinho ? (
            <Link
              to={main + "carrinho"}
              className={BOTAO + " mt-2 w-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"}
            >
              <i className="bi bi-cart-check" /> No carrinho
            </Link>
          ) : precoEbook > 0 ? (
            <button
              type="button"
              onClick={onComprar}
              className={BOTAO + " mt-2 w-full border border-line text-white/75 hover:border-insigne-orange/60 hover:text-white"}
            >
              <i className="bi bi-cart-plus" /> Comprar e-book · R$ {reais(precoEbook)}
            </button>
          ) : (
            <Link
              to={main + "livros/" + book.id}
              className={BOTAO + " mt-2 w-full border border-line text-white/75 hover:border-insigne-orange/60 hover:text-white"}
            >
              <i className="bi bi-cart-plus" /> Comprar e-book
            </Link>
          ))}

        {/* A mensagem é sobre ESTE título, então mora sob os botões dele. */}
        {book.mensagem && (
          <p className="mt-2 text-[11px] text-amber-200/90 leading-relaxed">
            <i className="bi bi-exclamation-triangle-fill" /> {book.mensagem}
          </p>
        )}
        {erroDownload && (
          <p className="mt-2 text-[11px] text-red-300 leading-relaxed">{erroDownload}</p>
        )}
      </div>
    </article>
  );
};

export default MeusLivros;
